// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { formatCount, pluralize } = require('shared/string-utils')
// Client V2 common imports
const { UserFollowCircleButton } = require('client-v2/common/components/buttons')
const LazyImage = require('client-v2/common/components/lazy-image')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { usePropsEventCallback } = require('client-v2/common/hooks/callbacks')
const userModelActions = require('client-v2/common/models/user/actions')
const tag = require('client-v2/common/utils/hyperscript')

const followers = pluralize('follower')

const bem = BemEntity('UserCard')

function UserCard(props) {
  const onCardClick = usePropsEventCallback(props, 'onCardClick')
  return (
    tag('div', {
      className: classNames(
        props.className,
        bem.block({
          'hrefs-enabled': props.isHrefsEnabled,
          interactive: !!props.onCardClick,
          self: props.isSelf
        })
      ),
      dataset: {
        userId: props.user.id
      }
    },
      tag('div', {
        className: bem.element('inner'),
        onClick: onCardClick
      },
        tag(UserCardHeaderImage, props),
        tag(UserCardAvatar, props),
        tag(UserCardNames, props),
        tag(UserCardFollowerCount, props),
        tag(UserCardBio, props),
        tag(UserCardFollowButton, props)
      )
    )
  )
}

UserCard.defaultProps = {
  // Flags
  isFollowerCountVisible: true,
  isHrefsEnabled: true,
  isSelf: false
}

if (__DEV__) {
  // Prop-checking imports
  const PropTypes = require('prop-types')
  const PageContextRecord = require('client-v2/common/models/page-context/record')
  const UserModelRecord = require('client-v2/common/models/user/record')

  UserCard.propTypes = {
    // Flags
    isFollowerCountVisible: PropTypes.bool,
    isHrefsEnabled: PropTypes.bool,
    isSelf: PropTypes.bool,
    // Values
    className: PropTypes.string,
    pageContext: PropTypes.shape(PageContextRecord.propTypes),
    user: PropTypes.shape(UserModelRecord.propTypes),
    // Callbacks
    onCardClick: PropTypes.func,
    onFollowClick: PropTypes.func,
    onFollowerCountClick: PropTypes.func
  }
}

module.exports = React.memo(UserCard)

// BEM Block entity Element components

function UserCardBio(props) {
  const { user } = props
  const bio = user.bio || (user.mixesBio && `Posts in ${user.mixesBio}`)
  if (!bio) {
    return null
  }
  return (
    tag('div', {
      className: bem.element('bio'),
      title: bio
    },
      bio
    )
  )
}

function UserCardNames(props) {
  const { user } = props
  const { 'display_name': displayName, username } = user
  return (
    tag('a', {
      className: bem.element('names'),
      href: userHref(user),
      title: `${displayName} @${username}`
    },
      tag('span', {
        className: bem.element('display-name')
      },
        displayName
      ),
      ' ',
      tag('span', {
        className: bem.element('username')
      },
        `@${username}`
      )
    )
  )
}

function UserCardFollowButton(props) {
  const onClick = usePropsEventCallback(props, 'onFollowClick')
  if (props.isSelf || !props.onFollowClick) {
    return null
  }
  return (
    tag(UserFollowCircleButton, {
      className: bem.element('follow-button'),
      checked: props.user.isFollowing,
      onClick
    })
  )
}

function UserCardFollowerCount(props) {
  const onClick = usePropsEventCallback(props, 'onFollowerCountClick')
  const count = props.user['follower_count']
  if (!props.isFollowerCountVisible || !count) {
    return null
  }
  return (
    tag('a', {
      className: bem.element('follower-count'),
      href: userFollowersHref(props),
      onClick
    },
      `${formatCount(count)} ${followers(count)}`
    )
  )
}

function UserCardHeaderImage(props) {
  const { user } = props
  let headerImageUrl = user['header_image_url']
  if (user['header_image_urls']) {
    headerImageUrl = user['header_image_urls'].small
  }
  if (!headerImageUrl) {
    return null
  }
  return (
    tag(LazyImage, {
      className: bem.element('header-image'),
      sources: [ headerImageUrl ]
    })
  )
}

function UserCardAvatar(props) {
  return (
    tag(UserAvatar, {
      className: bem.element('avatar'),
      href: userHref(props),
      user: props.user
    })
  )
}

// Linkage helpers

function userHref(props) {
  if (props.isHrefsEnabled) {
    return userModelActions.userProfileHref(props.user)
  }
}

function userFollowersHref(props) {
  if (props.isHrefsEnabled) {
    return userModelActions.userProfileFollowersHref(props.user)
  }
}
